import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {request} from "../lib/request";
import {clearToken, getToken, logoutAndRedirect, setToken} from "../lib/auth";
import {
    buildProfilePayload,
    clearCachedProfile,
    readCachedProfile,
    writeCachedProfile,
} from "../lib/profileCache";
import LogoutConfirmModal from "../components/LogoutConfirmModal";

const UserContext = createContext(null);

export const UserProvider = ({children}) => {
    const queryClient = useQueryClient();
    const [logoutModalOpen, setLogoutModalOpen] = useState(false);
    const [sessionActive, setSessionActive] = useState(() => !!getToken());

    const {data, isLoading, isFetching, isError, refetch} = useQuery({
        queryKey: ['restProfile'],
        queryFn: async () => await request({
            url: '/restaurant/panel',
            method: 'get',
        }),
        staleTime: 1000 * 30,
        gcTime: 1000 * 60 * 30,
        refetchOnWindowFocus: true,
        refetchInterval: 1000 * 30,
        enabled: sessionActive,
        retry: false,
        initialData: () => readCachedProfile() ?? undefined,
    });

    const activateSession = useCallback((token, restaurant) => {
        if (token) {
            setToken(token);
        }
        setSessionActive(true);

        if (restaurant) {
            const payload = buildProfilePayload(restaurant);
            queryClient.setQueryData(['restProfile'], payload);
            writeCachedProfile(payload);
        } else {
            queryClient.invalidateQueries({queryKey: ['restProfile']});
        }
    }, [queryClient]);

    const logout = () => {
        setLogoutModalOpen(true);
    };

    const closeLogoutModal = () => {
        setLogoutModalOpen(false);
    };

    const confirmLogout = () => {
        clearCachedProfile();
        logoutAndRedirect();
    };

    useEffect(() => {
        if (data?.data) {
            writeCachedProfile(data);
        }
    }, [data]);

    useEffect(() => {
        if (!isError) {
            return;
        }

        clearToken();
        clearCachedProfile();
        setSessionActive(false);
        queryClient.removeQueries({queryKey: ['restProfile']});
    }, [isError, queryClient]);

    const profile = data?.data;
    const hasProfile = profile != null;

    return (
        <>
            <UserContext.Provider value={{
                is_open: hasProfile ? profile.is_open : null,
                is_accepting_orders: hasProfile ? profile.is_accepting_orders : null,
                within_working_hours: hasProfile ? profile.within_working_hours : null,
                name: hasProfile ? profile.name : null,
                loading: sessionActive && !hasProfile && isLoading,
                isFetching,
                error: isError,
                sessionActive,
                refetch,
                activateSession,
                logout,
            }}>
                {children}
            </UserContext.Provider>

            <LogoutConfirmModal
                isOpen={logoutModalOpen}
                onClose={closeLogoutModal}
                onConfirm={confirmLogout}
            />
        </>
    );
};

export const useUser = () => {
    const ctx = useContext(UserContext);
    if (!ctx) throw new Error('useUser باید داخل UserProvider استفاده شود');
    return ctx;
};
